import React, { useState } from 'react';
import { Loader2, Lock, CheckCircle } from 'lucide-react';
import PinInput from './PinInput';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PinSetupModal - Mandatory PIN creation after first magic link login.
 * Cannot be closed/skipped. Internal users only (admin/worker).
 */
export default function PinSetupModal({ token, onComplete }) {
  const [step, setStep] = useState('create'); // 'create' | 'confirm' | 'done'
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleContinue = () => {
    if (pin.length !== 6) {
      setError('Ingresa 6 digitos');
      return;
    }
    setError('');
    setStep('confirm');
  };

  const handleSubmit = async () => {
    if (pinConfirm.length !== 6) {
      setError('Ingresa 6 digitos');
      return;
    }
    if (pin !== pinConfirm) {
      setError('Los PINs no coinciden. Intenta de nuevo.');
      setPinConfirm('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/auth/create-pin`,
        { pin, pin_confirm: pinConfirm },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('done');
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el PIN');
      if (err.response?.data?.detail?.includes('seguro')) {
        setStep('create');
        setPin('');
        setPinConfirm('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="pin-setup-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        {step === 'create' && (
          <>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'var(--ios-accent-bg, #FFF0ED)' }}>
              <Lock className="w-7 h-7" style={{ color: 'var(--ios-accent, #e03c3c)' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ios-text-primary)', textAlign: 'center', marginBottom: '8px' }}>
              Crea tu PIN de acceso rapido
            </h2>
            <p style={{ color: 'var(--ios-text-secondary)', fontSize: '14px', textAlign: 'center', marginBottom: '28px', lineHeight: '1.5' }}>
              Para que no tengas que abrir tu correo cada vez, crea un PIN de 6 digitos que usaras para entrar diario.
            </p>
            <PinInput value={pin} onChange={setPin} autoFocus />
            {error && <p className="text-center mt-3" style={{ color: '#dc2626', fontSize: '13px' }}>{error}</p>}
            <button
              onClick={handleContinue}
              disabled={pin.length !== 6}
              className="ios-btn-primary w-full py-3 mt-6"
              data-testid="pin-continue-btn"
            >
              Confirmar PIN
            </button>
            <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '12px', textAlign: 'center', marginTop: '16px' }}>
              Podras cambiarlo despues en Ajustes.
            </p>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'var(--ios-accent-bg, #FFF0ED)' }}>
              <Lock className="w-7 h-7" style={{ color: 'var(--ios-accent, #e03c3c)' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ios-text-primary)', textAlign: 'center', marginBottom: '8px' }}>
              Confirma tu PIN
            </h2>
            <p style={{ color: 'var(--ios-text-secondary)', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>
              Escribe tu PIN de 6 digitos otra vez para confirmar.
            </p>
            <PinInput value={pinConfirm} onChange={setPinConfirm} autoFocus />
            {error && <p className="text-center mt-3" style={{ color: '#dc2626', fontSize: '13px' }}>{error}</p>}
            <button
              onClick={handleSubmit}
              disabled={loading || pinConfirm.length !== 6}
              className="ios-btn-primary w-full py-3 mt-6"
              data-testid="pin-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Crear PIN'}
            </button>
            <button
              onClick={() => { setStep('create'); setPinConfirm(''); setError(''); }}
              className="w-full py-2 mt-2"
              style={{ color: 'var(--ios-text-secondary)', fontSize: '14px' }}
            >
              Volver atras
            </button>
          </>
        )}

        {step === 'done' && (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--ios-text-primary)', marginBottom: '8px' }}>
              PIN creado
            </h2>
            <p style={{ color: 'var(--ios-text-secondary)', fontSize: '14px' }}>
              La proxima vez podras entrar con tu correo y PIN.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
